import React, { useState } from 'react';
import { Share2, Sparkles, Sun, Moon, ChevronDown, ChevronUp, Check, CheckCircle2, RefreshCw } from 'lucide-react';

export default function MorningBriefingBanner({ briefing, loading, isCoachView, userProfile, phase, hasTrainedToday, physiologicalPhase }) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [copied, setCopied] = useState(false);
  const [customBriefing, setCustomBriefing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const activeBriefing = customBriefing || briefing;

  if (isCoachView) return null;

  if (userProfile?.subscriptionTier === 'FREE') {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.04)',
        padding: '1rem 1.25rem',
        borderRadius: '14px',
        fontSize: '0.88rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        border: '1px solid rgba(255, 255, 255, 0.08)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a78bfa', fontWeight: 700 }}>
          <Sparkles size={16} /> Insights de IA Diarios
        </div>
        <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>
          La Inteligencia Artificial te acompaña en cada fase del día adaptándose a tus entrenamientos y descanso.
        </p>
        <button 
          style={{ color: '#00e5ff', cursor: 'pointer', fontWeight: 600, background: 'none', border: 'none', padding: 0, textAlign: 'left', display: 'inline-block' }} 
          onClick={() => window.dispatchEvent(new CustomEvent('open-pricing-modal'))}
        >
          Actualizar a PRO
        </button>
      </div>
    );
  }

  const isPostWorkout = phase === 'post_workout' || hasTrainedToday;
  const currentHour = new Date().getHours();
  const isNight = currentHour >= 18;

  const config = isPostWorkout ? {
    title: 'Sesión de hoy completada: Análisis y Asimilación',
    badgeText: 'Post-Entreno IA ✅',
    Icon: CheckCircle2,
    bg: 'linear-gradient(135deg, rgba(6, 78, 59, 0.45) 0%, rgba(15, 23, 42, 0.95) 60%, rgba(20, 83, 45, 0.3) 100%)',
    border: 'rgba(16, 185, 129, 0.4)',
    iconColor: '#10b981',
    glowColor: '#10b981'
  } : isNight ? {
    title: 'Consejo para esta noche',
    badgeText: 'Análisis Nocturno IA',
    Icon: Moon,
    bg: 'linear-gradient(135deg, rgba(30, 27, 75, 0.85) 0%, rgba(15, 23, 42, 0.95) 100%)',
    border: 'rgba(139, 92, 246, 0.35)',
    iconColor: '#a78bfa',
    glowColor: '#8b5cf6'
  } : {
    title: 'Consejo para hoy',
    badgeText: 'Diagnóstico Diario IA',
    Icon: Sun,
    bg: 'linear-gradient(135deg, rgba(6, 78, 59, 0.35) 0%, rgba(15, 23, 42, 0.95) 60%, rgba(30, 41, 59, 0.8) 100%)',
    border: 'rgba(249, 115, 22, 0.35)',
    iconColor: '#fb923c',
    glowColor: '#f97316'
  };

  const handleForceRefresh = async (e) => {
    e?.stopPropagation?.();
    if (refreshing) return;
    setRefreshing(true);
    try {
      const url = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
      const token = localStorage.getItem('token');
      const res = await fetch(`${url}/ai/morning-briefing?force=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.briefing) {
          setCustomBriefing(data.briefing);
        }
      }
    } catch (e) {
      console.error("Error al actualizar consejo de IA:", e);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !customBriefing) {
    return (
      <div className="fade-in" style={{
        background: 'rgba(15, 23, 42, 0.7)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderLeft: `4px solid ${config.iconColor}`,
        padding: '1.2rem 1.4rem',
        borderRadius: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div className="skeleton-pulse" style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div className="skeleton-pulse" style={{ height: '14px', width: '35%', background: 'rgba(255,255,255,0.1)', borderRadius: '4px' }} />
          <div className="skeleton-pulse" style={{ height: '14px', width: '85%', background: 'rgba(255,255,255,0.08)', borderRadius: '4px' }} />
        </div>
      </div>
    );
  }

  if (!activeBriefing) return null;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${config.title} - Coach IA`,
          text: activeBriefing
        });
      } catch (err) {
        // Ignorar si el usuario cancela
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(activeBriefing);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      } catch (err) {
        console.error('Error al copiar al portapapeles:', err);
      }
    }
  };

  const { Icon } = config;

  // MODO PÍLDORA COLAPSADA (Permite al usuario ahorrar espacio sin perder nunca el mensaje)
  if (isCollapsed) {
    return (
      <div 
        onClick={() => setIsCollapsed(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsCollapsed(false); } }}
        role="button"
        tabIndex={0}
        style={{
          background: 'rgba(15, 23, 42, 0.75)',
          border: `1px solid ${config.border}`,
          borderRadius: '12px',
          padding: '0.7rem 1.15rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box',
          width: '100%',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
        onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)'}
        onMouseOut={e => e.currentTarget.style.background = 'rgba(15, 23, 42, 0.75)'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden' }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${config.border}`,
            flexShrink: 0
          }}>
            <Icon size={15} color={config.iconColor} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <span style={{
              fontSize: '0.7rem',
              color: config.iconColor,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              background: 'rgba(255,255,255,0.06)',
              padding: '2px 6px',
              borderRadius: '5px',
              flexShrink: 0
            }}>
              {config.badgeText}
            </span>
            <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'white' }}>
              {config.title}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: 400, textOverflow: 'ellipsis', overflow: 'hidden' }}>
              · Toca para ver el diagnóstico
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.76rem', color: config.iconColor, fontWeight: 700, flexShrink: 0, marginLeft: '12px' }}>
          <span>Desplegar</span>
          <ChevronDown size={15} />
        </div>
      </div>
    );
  }

  // MODO TARJETA COMPLETA EXPANDIDA
  return (
    <div className="fade-in" style={{
      background: config.bg,
      border: `1px solid ${config.border}`,
      borderRadius: '16px',
      padding: '1.25rem 1.4rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      position: 'relative',
      overflow: 'hidden',
      boxSizing: 'border-box',
      width: '100%',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)'
    }}>
      {/* Resplandor decorativo sutil (iOS WebKit compatible, sin mix-blend-mode) */}
      <div style={{
        position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px',
        background: config.glowColor, filter: 'blur(70px)', opacity: 0.15, borderRadius: '50%', pointerEvents: 'none'
      }} />

      {/* Header del Banner */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${config.border}`
          }}>
            <Icon size={18} color={config.iconColor} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {config.badgeText}
              </span>
              <Sparkles size={12} color={config.iconColor} />
              {physiologicalPhase?.label && (
                <span style={{
                  fontSize: '0.68rem',
                  background: `${physiologicalPhase.color || '#00e5ff'}20`,
                  color: physiologicalPhase.color || '#00e5ff',
                  border: `1px solid ${physiologicalPhase.color || '#00e5ff'}40`,
                  padding: '1px 7px',
                  borderRadius: '10px',
                  fontWeight: 700
                }}>
                  {physiologicalPhase.label}
                </span>
              )}
            </div>
            <h4 style={{ margin: '2px 0 0 0', fontSize: '1.02rem', fontWeight: 800, color: 'white' }}>
              {config.title}
            </h4>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
          <button 
            type="button"
            title="Regenerar consejo con IA"
            onClick={handleForceRefresh}
            disabled={refreshing}
            style={{
              background: 'rgba(255,255,255,0.06)', 
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', 
              padding: '6px 10px',
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              cursor: refreshing ? 'wait' : 'pointer', 
              color: 'white', 
              fontSize: '0.72rem',
              fontWeight: 600,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? 'Generando...' : 'Regenerar'}</span>
          </button>

          <button 
            type="button"
            title="Compartir o copiar consejo"
            onClick={handleShare}
            style={{
              background: 'rgba(255,255,255,0.06)', 
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', 
              padding: '6px 10px',
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              cursor: 'pointer', 
              color: 'white', 
              fontSize: '0.72rem',
              fontWeight: 600,
              transition: 'all 0.2s ease'
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
          >
            {copied ? (
              <>
                <Check size={13} color="#34d399" />
                <span style={{ color: '#34d399' }}>Copiado</span>
              </>
            ) : (
              <>
                <Share2 size={13} />
                <span>Compartir</span>
              </>
            )}
          </button>

          <button 
            type="button"
            title="Plegar consejo"
            onClick={() => setIsCollapsed(true)}
            style={{
              background: 'rgba(255,255,255,0.06)', 
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', 
              width: '30px', 
              height: '30px',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: 'pointer', 
              color: 'rgba(255,255,255,0.7)', 
              transition: 'all 0.2s ease'
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
          >
            <ChevronUp size={16} />
          </button>
        </div>
      </div>
      
      {/* Contenido del Mensaje de IA */}
      <div style={{ position: 'relative', zIndex: 1, paddingTop: '0.1rem' }}>
        <div style={{
          fontSize: '0.88rem', 
          color: 'rgba(255,255,255,0.92)', 
          lineHeight: '1.6', 
          margin: 0,
          textAlign: 'left',
          fontWeight: 400,
          whiteSpace: 'pre-line'
        }}>
          {activeBriefing}
        </div>
      </div>
    </div>
  );
}
